import { Injectable } from '@angular/core';
import { ApiHttpClient, ListOption, ListOptionRequest } from '../modules/tcm-core.module';
import { AppointmentDetailsForSalesOrderViewModel, AppointmentListOptionRequest, CalendarReportSearchRequest, ReferringPhysicianPdfLetterRequest, AppointmentRecommendationCheckRequest, RecallSetRequest } from '../models/appointments';
import { AppointmentResourceViewModel } from '../models/appointments/appointment-request-view-model';
import { AppointmentValidationRequest } from '../models/appointments/appointment-validation-request';
import { AppointmentValidationResultViewModel } from '../models/appointments/appointment-validation-result-view-model';
import { RecommendationSideEnum } from '../models/enums';
import { AppointmentStatusChangeResponse } from '../models/appointments/appointment-status-change-response';
import { FileResponse } from '../modules/tcm-core.module/models/files/file-response';

@Injectable({
  providedIn: 'root'
})
export class AppointmentDataService {
    
  constructor(private readonly httpClient: ApiHttpClient) {
  }

  public finishReschedule : boolean;
  public getAppointmentOptionsAsync(patientId: number, clinicId: number, timeout: Promise<any>): Promise<ListOption<number>[]> {
    const request: AppointmentListOptionRequest = { searchString: null, patientId: patientId, clinicId: clinicId };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('Appointments/Options', request, timeout);
  }

  public getRecallReasonsAsync(timeout: Promise<any>): Promise<ListOption<number>[]> {
    return this.httpClient.getJsonAsync<ListOption<number>[]>('Appointments/RecallReasons', {}, timeout);
  }

  public setRecallAsync(request: RecallSetRequest, timeout: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync<any>('Appointments/SetRecall', request, timeout);
  }

  public getCancellationReasonsAsync(timeout: Promise<any>): Promise<ListOption<number>[]> {
    return this.httpClient.getJsonAsync<ListOption<number>[]>('Appointments/CancellationReasons', {}, timeout);
  }

  public getAppointmentDetailsForSalesOrderAsync(id: number, timeout: Promise<any>): Promise<AppointmentDetailsForSalesOrderViewModel> {
    const request = { id: id };
    return this.httpClient.getJsonAsync<AppointmentDetailsForSalesOrderViewModel>('Appointments/DetailsForSalesOrder', request, timeout);
  }

  public cancelAppointmentAsync(appointmentId: number, cancellationReasonId: number, timeout: Promise<any>): Promise<AppointmentStatusChangeResponse> {
    return this.httpClient.getJsonAsync('Appointments/Cancel', { appointmentId, cancellationReasonId }, timeout, false);
  }

  public getAppointmentHaNeededAsync(appointmentId: number, timeout: Promise<any>): Promise<RecommendationSideEnum> {
    return this.httpClient.getJsonAsync<RecommendationSideEnum>('Appointments/GetAppointmentHaNeeded', { appointmentId }, timeout, false);
  }

  public getNoahActionHaNeededAsync(noahActionDataId: number, timeout: Promise<any>): Promise<RecommendationSideEnum> {
    return this.httpClient.getJsonAsync<RecommendationSideEnum>('Appointments/GetNoahActionHaNeeded', { noahActionDataId }, timeout, false);
  }

  public setAppointmentCheckOut(appointmentId: number, timeout: Promise<any>): Promise<AppointmentStatusChangeResponse> {
    return this.httpClient.getJsonAsync('Appointments/SetCheckOut', { appointmentId }, timeout, false);
  }

  public setAppointmentCheckIn(appointmentId: number, timeout: Promise<any>): Promise<AppointmentStatusChangeResponse> {
    return this.httpClient.getJsonAsync('Appointments/SetCheckIn', { appointmentId }, timeout, false);
  }

  public confirmAppointmentAsync(appointmentId: number, confirmationSourceId: number, timeout: Promise<any>): Promise<any> {
    return this.httpClient.getJsonAsync('Appointments/Confirm', { appointmentId, confirmationSourceId}, timeout, false);
  }

  public checkAppointmentHasDifferrentRecommendationAsync(request: AppointmentRecommendationCheckRequest, timeout: Promise<any>): Promise<any> {
    return this.httpClient.getJsonAsync('Appointments/CheckAppointmentHasDifferrentRecommendation', request, timeout, false);
  }

  public generateLettersAsync(selectedLineIds: number[], timeout: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync('Appointments/GenerateLetters', selectedLineIds, timeout);
  }

  public generatePdfLettersAsync(selectedLineIds: number[], timeout: Promise<any>): Promise<FileResponse> {
    const request: ReferringPhysicianPdfLetterRequest = { appointmentIds: selectedLineIds };
    return this.httpClient.getFileAsync('Appointments/GeneratePdfLetters', request, timeout);
  }

  public generateCalendarReportAsync(request: CalendarReportSearchRequest, timeout: Promise<any>): Promise<FileResponse> {
    return this.httpClient.getFileAsync('Appointments/GenerateCalendarReport?request=' + encodeURIComponent(JSON.stringify(request)), timeout);
  }

  public getOptionsForNoteAsync(query: string, cancellationToken: Promise<any> = null): Promise<ListOption<number>[]> {
    const request: ListOptionRequest = { searchString: query };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('Appointments/OptionsForNote', request, cancellationToken);
  }

  public hasResourceBookedThisTimeAsync(request: AppointmentResourceViewModel, cancellationToken: Promise<any>): Promise<AppointmentResourceViewModel> {
    return this.httpClient.getJsonAsync<AppointmentResourceViewModel>('Appointments/HasResourceBookedThisTime', request, cancellationToken);
  }

  public getLinkedMedicalReportIdAsync(appointmentId: number, salesOrderId: number, timeout: Promise<any>): Promise<number> {
    return this.httpClient.getJsonAsync<number>('Appointments/GetLinkedMedicalReportId', { appointmentId, salesOrderId }, timeout, false);
  }

  public checkMedicalReportExistAsync(medicalReportId: number, appointmentId: number, timeout: Promise<any>): Promise<boolean> {
    return this.httpClient.getJsonAsync('Appointments/CheckMedicalReportExist', { medicalReportId, appointmentId }, timeout, false);
  }
}
